import { useEffect, useMemo, useState } from 'react'
import { Box } from '@chakra-ui/react'
import ReportPageHeader from '@/components/reports/ReportPageHeader'
import KpiReportLayout from '@/components/reports/KpiReportLayout'
import {
  buildFeSummaryRows,
  FE_TASK_DETAIL_COLUMNS,
  formatFilterRange,
  formatThresholds,
} from '@/components/reports/kpiReportColumns'
import { helpdeskReportService, type FeKpiResponse, type ReportFilters } from '@/lib/services/helpdeskReportService'

const emptyData: FeKpiResponse = {
  summary: {},
  metric_breakdown: [],
  details: {},
  filters: {},
}

export default function FeKpiReportPage() {
  const [filters, setFilters] = useState<ReportFilters>({})
  const [data, setData] = useState<FeKpiResponse>(emptyData)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    setLoading(true)
    helpdeskReportService
      .getFeKpi(filters)
      .then(setData)
      .catch(() => setData(emptyData))
      .finally(() => setLoading(false))
  }, [filters.from, filters.to, filters.client_id])

  const periodLabel = formatFilterRange(data.filters ?? filters)

  const detailTabs = useMemo(
    () => [
      {
        label: 'Tasks',
        columns: FE_TASK_DETAIL_COLUMNS,
        rows: data.details?.tasks?.rows ?? [],
        total: data.details?.tasks?.total ?? 0,
        shown: data.details?.tasks?.shown ?? 0,
        rowKey: (row: Record<string, unknown>, index: number) => String(row.task_id ?? index),
      },
    ],
    [data.details]
  )

  return (
    <Box p={4}>
      <ReportPageHeader
        title="FE KPI"
        subtitle="Field engineer task acceptance, resolution, and APT metrics"
        reportType="fe-kpi"
        filters={filters}
        onFiltersChange={setFilters}
      />
      <KpiReportLayout
        loading={loading}
        periodLabel={periodLabel}
        thresholdNote={formatThresholds(data.thresholds)}
        summaryRows={buildFeSummaryRows(data.summary)}
        breakdownRows={data.metric_breakdown ?? []}
        detailTabs={detailTabs}
      />
    </Box>
  )
}
